import { useState } from 'react';
import { PhoneIncoming, CheckCircle2 } from 'lucide-react';
import { formatPhoneInput } from '../../shared/EstimateEnhancements';
import { API_URL } from '../../config/api';

// One-field "call me back" request: texts + emails the store instantly.
export const CallbackRequest = ({ idPrefix, open, nextOpen, deviceQuery, offerPrice, condition }) => {
  const [expanded, setExpanded] = useState(false);
  const [phone, setPhone] = useState('');
  const [state, setState] = useState('idle');
  const [error, setError] = useState('');
  const valid = phone.replace(/\D/g, '').length === 10;

  const submit = async () => {
    if (!valid) return;
    setState('sending'); setError('');
    try {
      const r = await fetch(`${API_URL}/api/estimate/callback-request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, device_query: deviceQuery, offer_price: offerPrice || 0, condition, source: idPrefix }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Could not send, please call us instead.');
      setState('sent');
    } catch (e) { setError(e.message); setState('idle'); }
  };

  if (state === 'sent') {
    return (
      <p role="status" data-testid={`${idPrefix}-callback-success`} className="flex items-center justify-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2.5 text-sm font-semibold text-emerald-800">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        {open ? "Got it! We'll call you within 10 minutes. A confirmation text is on its way." : `Got it! We'll call you when we open ${nextOpen}. A confirmation text is on its way.`}
      </p>
    );
  }
  if (!expanded) {
    return (
      <button type="button" onClick={() => setExpanded(true)} data-testid={`${idPrefix}-callback-toggle`}
        className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100">
        <PhoneIncoming className="h-4 w-4 shrink-0" /> Request a callback
      </button>
    );
  }
  return (
    <div className="space-y-2" data-testid={`${idPrefix}-callback-form`}>
      <p className="text-xs text-slate-600 text-center">{open ? "Leave your number, we'll call you in 10 minutes." : `Leave your number, we'll call you when we open ${nextOpen}.`}</p>
      <div className="flex gap-2">
        <input type="tel" inputMode="tel" autoComplete="tel" value={phone} placeholder="(555) 123-4567"
          onChange={e => setPhone(formatPhoneInput(e.target.value))} aria-label="Your phone number"
          data-testid={`${idPrefix}-callback-phone`}
          className="min-h-[44px] flex-1 min-w-0 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600" />
        <button type="button" onClick={submit} disabled={!valid || state === 'sending'} data-testid={`${idPrefix}-callback-submit`}
          className="min-h-[44px] rounded-lg bg-emerald-700 px-4 text-sm font-semibold text-white transition-colors hover:bg-emerald-800 disabled:opacity-40">
          {state === 'sending' ? 'Sending…' : 'Call me'}
        </button>
      </div>
      <p className="text-[10px] text-slate-500 text-center" data-testid={`${idPrefix}-callback-consent`}>By tapping Call me, you agree to get one confirmation text. Msg &amp; data rates may apply.</p>
      {error && <p role="alert" data-testid={`${idPrefix}-callback-error`} className="text-xs text-red-700 text-center">{error}</p>}
    </div>
  );
};
